import React, { useState, useEffect, useRef } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useData } from '../context/DataContext';
import { useSync } from '../context/SyncContext';
import { Staff, SubscriptionFeature } from '../types';
import { NOTIFICATION_SOUND } from '../constants';
import { Logo } from './Logo';
import { useTranslation } from 'react-i18next';
import { useToast } from '../context/ToastContext';
import { toggleTheme, getThemeToggleLabel } from '../src/utils/theme';

interface LayoutProps {
    children: React.ReactNode;
}
type SidebarMenuItem = {
    name: string;
    icon: string;
    path: string;
    badge?: number;
    badgeColor?: string;
    permission?: string;
    feature?: SubscriptionFeature;
};

const ThemeToggleButton: React.FC<{ className: string }> = ({ className }) => (
    <button
        type="button"
        data-theme-toggle
        onClick={toggleTheme}
        aria-label={getThemeToggleLabel()}
        title={getThemeToggleLabel()}
        className={`${className} size-10 shrink-0 items-center justify-center rounded-full text-text-secondary hover:text-primary transition-colors`}
    >
        <span className="material-symbols-outlined theme-icon-to-light">light_mode</span>
        <span className="material-symbols-outlined theme-icon-to-dark">dark_mode</span>
    </button>
);

const Layout: React.FC<LayoutProps> = ({ children }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const { tables, currentUser, cloudUser, isAuthReady, setCurrentUser, staff, hasPermission, hasFeature, appSettings, updateAppSettings, generateShiftReport, lastNotification, qrMenuSettings, isOfflineSimulated, cafeId, transactions, closeDay, updateStaff } = useData();
    const { downloadFromCloud, hasLocalPendingSync } = useSync();
    const { showToast } = useToast();

    // --- STATE KONTROLLERİ ---
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
    const [isRecovering, setIsRecovering] = useState(false);
    const [isPinModalOpen, setIsPinModalOpen] = useState(false);
    const [targetUser, setTargetUser] = useState<Staff | null>(null);
    const [pinInput, setPinInput] = useState('');
    const [pinError, setPinError] = useState(false);
    const [isVerifyingPin, setIsVerifyingPin] = useState(false);
    const [isOnline, setIsOnline] = useState(navigator.onLine);
    const lastPlayedRef = useRef<number>(0);
    const audioCtxRef = useRef<AudioContext | null>(null);
    const transactionsRef = useRef(transactions);

    // --- SMART HEADER LOGIC (Sadece Mobil) ---
    const [isHeaderVisible, setIsHeaderVisible] = useState(true);
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const lastScrollY = useRef(0);

    const handleMainScroll = () => {
        if (!scrollContainerRef.current) return;
        // Eğer cihaz tablet veya PC ise (1024px ve üzeri) bu mantığı hiç çalıştırma
        if (window.innerWidth >= 1024) return;

        const currentScrollY = scrollContainerRef.current.scrollTop;
        
        if (currentScrollY > lastScrollY.current && currentScrollY > 60) {
            // Aşağı kaydırılıyor ve en üstten biraz uzaklaşıldı (Header gizle)
            if (isHeaderVisible) setIsHeaderVisible(false);
        } else if (currentScrollY < lastScrollY.current) {
            // Yukarı kaydırılıyor (Header göster)
            if (!isHeaderVisible) setIsHeaderVisible(true);
        }
        lastScrollY.current = currentScrollY;
    };

    // --- AUTH & RECOVERY LOGIC ---
    useEffect(() => {
        if (!isAuthReady) return;
        if (!cloudUser) { navigate('/login'); return; }
        if (appSettings.isSetupComplete === false) {
            if (cafeId && !isRecovering) {
                const attemptRecovery = async () => {
                    setIsRecovering(true);
                    try {
                        if (await hasLocalPendingSync()) return;
                        await downloadFromCloud();
                    } catch (e) { navigate('/setup'); } finally { setIsRecovering(false); }
                };
                attemptRecovery();
            } else if (!cafeId && !isRecovering) { navigate('/setup'); }
        }
    }, [isAuthReady, cloudUser, appSettings.isSetupComplete, navigate, cafeId, downloadFromCloud, hasLocalPendingSync]);

    useEffect(() => {
        const handleOnline = () => setIsOnline(true);
        const handleOffline = () => setIsOnline(false);
        window.addEventListener('online', handleOnline);
        window.addEventListener('offline', handleOffline);
        return () => {
            window.removeEventListener('online', handleOnline);
            window.removeEventListener('offline', handleOffline);
        };
    }, []);

    useEffect(() => {
        const unlock = () => {
            if (!audioCtxRef.current) {
                audioCtxRef.current = new (window.AudioContext ||
                    (window as any).webkitAudioContext)();
            }
            if (audioCtxRef.current.state === 'suspended') {
                audioCtxRef.current.resume();
            }
        };
        document.addEventListener('click', unlock);
        return () => document.removeEventListener('click', unlock);
    }, []);

    // --- NOTIFICATION LOGIC ---
    useEffect(() => {
        if (lastNotification && lastNotification.timestamp > lastPlayedRef.current) {
            lastPlayedRef.current = lastNotification.timestamp;
            const table = tables.find(t => t.id === lastNotification.tableId);
            if (table) {
                showToast(`🔔 Yeni Sipariş: ${table.name}`, 'info', { id: String(table.id), type: 'qrOrder' });
                if (qrMenuSettings.soundEnabled) {
                    try {
                        if (!audioCtxRef.current) {
                            audioCtxRef.current = new (window.AudioContext ||
                                (window as any).webkitAudioContext)();
                        }
                        const ctx = audioCtxRef.current;
                        if (ctx.state === 'suspended') ctx.resume();
                        const playBeep = (t: number, freq: number) => {
                            const osc = ctx.createOscillator();
                            const gain = ctx.createGain();
                            osc.connect(gain);
                            gain.connect(ctx.destination);
                            osc.frequency.value = freq;
                            osc.type = 'sine';
                            gain.gain.setValueAtTime(1.0, t);
                            gain.gain.exponentialRampToValueAtTime(0.01, t + 0.4);
                            osc.start(t);
                            osc.stop(t + 0.4);
                        };
                        const now = ctx.currentTime;
                        playBeep(now, 880);
                        playBeep(now + 0.45, 1100);
                        playBeep(now + 0.9, 1320);
                    } catch(e) {}
                }
            }
        }
    }, [lastNotification, tables, qrMenuSettings.soundEnabled]);
              
        // --- OTOMATİK Z-RAPORU ROBOTU ---
        useEffect(() => { transactionsRef.current = transactions; }, [transactions]);

        useEffect(() => {
            if (!appSettings.autoZReport || !isAuthReady) return;

            const isRunning = { current: false };

            const getBusinessDayKey = (isoDate: string): string => {
                const startHour = appSettings.workingHoursStart ?? 8;
                const d = new Date(isoDate);
                const bd = new Date(d);
                if (d.getHours() < startHour) bd.setDate(bd.getDate() - 1);
                const y = bd.getFullYear();
                const mo = String(bd.getMonth() + 1).padStart(2, '0');
                const da = String(bd.getDate()).padStart(2, '0');
                return `${y}-${mo}-${da}`;
            };

            const checkAndCloseDay = async () => {
                if (isRunning.current) return;
                isRunning.current = true;
                try {
                    const activeTrx = transactionsRef.current.filter(t => !t.isArchived);
                    if (activeTrx.length === 0) return;

                    const now = new Date();
                    const startHour = appSettings.workingHoursStart ?? 8;
                    const endHour = appSettings.workingHoursEnd ?? 23;

                    // Bugünün mesai günü anahtarı
                    const todayBD = new Date(now);
                    if (now.getHours() < startHour) todayBD.setDate(todayBD.getDate() - 1);
                    const todayKey = `${todayBD.getFullYear()}-${String(todayBD.getMonth() + 1).padStart(2, '0')}-${String(todayBD.getDate()).padStart(2, '0')}`;

                    // Kapatılmamış işlemlerin mesai günlerini bul
                    const mappedDays: string[] = activeTrx.map(t => getBusinessDayKey(t.date || ''));
                    const pendingDays: string[] = mappedDays.filter((v, i, a) => a.indexOf(v) === i).sort();

                    // Bugünden önceki günler → kesinlikle kapat
                    const pastDays: string[] = pendingDays.filter(d => d < todayKey);

                    // Bugün → sadece mesai bitiş saati geldiyse kapat
                    const todayHasTrx = pendingDays.includes(todayKey);
                    const todayDone = todayHasTrx && now.getHours() >= endHour && endHour > startHour;

                    const daysToClose: string[] = [...pastDays, ...(todayDone ? [todayKey] : [])];
                    if (daysToClose.length === 0) return;

                    // localStorage'daki kapatılmış günleri kontrol et (aynı günü iki kez kapatma)
                    const closedDays: string[] = JSON.parse(localStorage.getItem('autoClosedDays') || '[]');
                    const newDays = daysToClose.filter(d => !closedDays.includes(d));
                    if (newDays.length === 0) return;

                    // Her günü sırayla kapat
                    for (const day of newDays) {
                        await closeDay(day);
                        closedDays.push(day);
                    }
                    localStorage.setItem('autoClosedDays', JSON.stringify(closedDays));

                    // Uyarı göster
                    const msg = newDays.length === 1
                        ? `${newDays[0]} için otomatik Z-Raporu alındı.`
                        : `${newDays.length} gün için otomatik Z-Raporu alındı (${newDays.join(', ')}).`;
                    showToast(msg, 'success');
                } finally {
                    isRunning.current = false;
                }
            };

            checkAndCloseDay();
            const interval = setInterval(checkAndCloseDay, 60000);
            return () => clearInterval(interval);
        }, [appSettings.autoZReport, appSettings.workingHoursEnd, appSettings.workingHoursStart, isAuthReady]);

    // Tekrarlayan gider hatirlaticisi artik tek yerden (src/hooks/useExpenseReminders.ts,
    // App.tsx'teki ExpenseReminderCheck uzerinden) yonetiliyor - burada tekrar edilmiyor.

              // --- YARDIMCI FONKSİYONLAR ---

    // --- YARDIMCI FONKSİYONLAR ---
    const getInitials = (name: string) => name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
    const getRoleLabel = (role: string) => {
        const keyMap: Record<string, string> = { 'Yönetici': 'admin', 'Barista': 'barista', 'Garson': 'waiter', 'Mutfak': 'kitchen' };
        const key = keyMap[role]; return key ? t(`roles.${key}`) : role;
    };
    const getRoleColor = (role: string) => {
        switch (role) {
            case 'Yönetici': return 'text-primary bg-primary/10 border-primary/20';
            case 'Barista': return 'text-orange-400 bg-orange-400/10 border-orange-400/20';
            case 'Mutfak': return 'text-accent-green bg-accent-green/10 border-accent-green/20';
            case 'Garson': return 'text-accent-blue bg-accent-blue/10 border-accent-blue/20';
            default: return 'text-text-secondary bg-background-main border-border-color';
        }
    };

    const setStaffStatus = async (id: number, status: 'working' | 'off'): Promise<Staff | undefined> => {
        const latest = staff.find(s => s.id === id);
        if (!latest || latest.status === status) return latest;
        const updated = { ...latest, status };
        await updateStaff(updated);
        return updated;
    };

    const handleProfileClick = async (user: Staff) => {
        const isSwitchingUser = currentUser && currentUser.id !== user.id;
        if (isSwitchingUser && appSettings.autoShiftReport) await generateShiftReport(currentUser);
        if (isSwitchingUser) await setStaffStatus(currentUser.id, 'off');
        if (user.hasPinCode) {
            setTargetUser(user); setIsProfileModalOpen(false); setIsPinModalOpen(true); setPinInput(''); setPinError(false);
        } else {
            const updated = await setStaffStatus(user.id, 'working');
            setCurrentUser(updated ?? user); setIsProfileModalOpen(false); navigate('/');
        }
    };

    const handlePinSubmit = async () => {
        if (!targetUser || isVerifyingPin || !cafeId) return;
        setIsVerifyingPin(true);
        try {
            const { httpsCallable } = await import('firebase/functions');
            const { functions } = await import('../src/firebase');
            const verifyStaffPin = httpsCallable(functions, 'verifyStaffPin');
            await verifyStaffPin({ cafeId, staffId: targetUser.id, pin: pinInput });

            const updated = await setStaffStatus(targetUser.id, 'working');
            setCurrentUser(updated ?? targetUser); setIsPinModalOpen(false); setTargetUser(null); navigate('/');
        } catch (e: any) {
            console.error('Staff PIN verification failed:', e);
            if (e?.code === 'functions/resource-exhausted') {
                alert(e.message || 'Çok fazla hatalı deneme. Lütfen daha sonra tekrar deneyin.');
            } else {
                setPinError(true); setPinInput(''); setTimeout(() => setPinError(false), 500);
            }
        } finally {
            setIsVerifyingPin(false);
        }
    };

    const handlePinInput = (num: string) => { if (!isVerifyingPin && pinInput.length < 4) setPinInput(prev => prev + num); };
    const handlePinBackspace = () => { if (!isVerifyingPin) setPinInput(prev => prev.slice(0, -1)); };

    useEffect(() => {
        if (!isPinModalOpen) return;
        const handleKeyDown = (e: KeyboardEvent) => {
            if (isVerifyingPin) return;
            if (e.key >= '0' && e.key <= '9') handlePinInput(e.key);
            else if (e.key === 'Backspace') handlePinBackspace();
            else if (e.key === 'Enter') handlePinSubmit();
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isPinModalOpen, pinInput, targetUser, isVerifyingPin]);

    const handleLogout = async () => {
        if (currentUser && appSettings.autoShiftReport) await generateShiftReport(currentUser);
        if (currentUser) await setStaffStatus(currentUser.id, 'off');
        navigate('/login');
    };

    // --- MENÜ ÖĞELERİ ---
    const incomingOrderCount = tables.filter(t => t.incomingOrder && t.incomingOrder.length > 0).length;
    const activeOrderCount = tables.filter(t => t.status === 'occupied' && t.orders && t.orders.length > 0).length;
    const menuItems: SidebarMenuItem[] = [
        { name: t('sidebar.dashboard'), icon: 'storefront', path: '/' },
        { name: t('sidebar.tables'), icon: 'grid_view', path: '/tables', badge: incomingOrderCount || activeOrderCount || undefined, badgeColor: incomingOrderCount > 0 ? 'bg-orange-500 animate-pulse' : 'bg-accent-red', permission: 'VIEW_TABLES' },
        { name: t('sidebar.staff'), icon: 'groups', path: '/staff' },
        { name: t('sidebar.loyalty'), icon: 'loyalty', path: '/loyalty', permission: 'APPLY_DISCOUNT' },
        { name: t('sidebar.qr_menu'), icon: 'qr_code_2', path: '/qr-menu', permission: 'MANAGE_SETTINGS' },
        { name: t('sidebar.menu_product'), icon: 'restaurant_menu', path: '/menu', permission: 'MANAGE_MENU' },
        { name: t('sidebar.stock_recipe'), icon: 'inventory_2', path: '/stock', permission: 'MANAGE_STOCK', feature: 'inventory_tracking' },
        { name: t('sidebar.reports'), icon: 'bar_chart', path: '/reports', permission: 'VIEW_REPORTS' },
        { name: t('sidebar.settings'), icon: 'settings', path: '/settings', permission: 'MANAGE_SETTINGS' },
    ];

    const visibleMenuItems = menuItems.filter(item =>
        (!item.permission || hasPermission(item.permission)) &&
        (!item.feature || hasFeature(item.feature)) &&
        !(appSettings.hiddenNavItems || []).includes(item.path)
    );

    if (!isAuthReady || isRecovering) return (
        <div className="flex h-screen w-full items-center justify-center bg-background-main text-fg">
            <div className="flex flex-col items-center">
                <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mb-6"></div>
                <h2 className="text-xl font-bold">{isRecovering ? t('auth.recovering') : 'KahveInn...'}</h2>
            </div>
        </div>
    );

    return (
        <div className="fixed inset-0 flex bg-background-main text-fg selection:bg-primary/30 overflow-hidden w-full h-[100dvh]">
            
            {/* OFFLINE BLOCKER */}
            {((!isOnline || isOfflineSimulated) && !hasFeature('offline_mode')) && (
                <div className="fixed inset-0 z-[9999] bg-black/95 backdrop-blur-md flex items-center justify-center p-8">
                    <div className="max-w-md w-full text-center space-y-6">
                        <span className="material-symbols-outlined text-6xl text-accent-red animate-pulse">wifi_off</span>
                        <h2 className="text-3xl font-black">İnternet Bağlantısı Gerekli</h2>
                        <p className="text-text-secondary">Lite paketi çevrimdışı çalışmayı desteklemez.</p>
                    </div>
                </div>
            )}

            {/* MOBILE OVERLAY */}
            {isMobileMenuOpen && <div className="fixed inset-0 z-[150] bg-black/60 backdrop-blur-sm lg:hidden" onClick={() => setIsMobileMenuOpen(false)} />}

            {/* SIDEBAR */}
            <aside className={`fixed inset-y-0 left-0 z-[160] w-72 bg-background-main border-r border-border-color transform transition-transform duration-300 lg:relative lg:translate-x-0 flex flex-col h-full ${isMobileMenuOpen ?
'translate-x-0 shadow-2xl' : '-translate-x-full lg:translate-x-0'} lg:w-20 xl:w-64`}>
            <div className="p-4 lg:p-3 xl:p-6 flex flex-col gap-6 flex-1 overflow-y-auto lg:overflow-x-hidden custom-scrollbar">
                    <div className="flex items-center justify-between lg:justify-center xl:justify-start gap-3">
                        <div className="h-10 w-10 lg:h-12 rounded-full bg-primary text-white flex items-center justify-center shadow-lg"><Logo className="w-6 h-6" /></div>
                        <div className="flex flex-col lg:hidden xl:flex">
                            <h1 className="text-lg font-bold truncate">{appSettings.appName || 'KahveInn'}</h1>
                            <p className="text-text-secondary text-xs">{t('sidebar.admin_panel')}</p>
                        </div>
                        <button onClick={() => setIsMobileMenuOpen(false)} className="lg:hidden text-text-secondary"><span className="material-symbols-outlined">menu_open</span></button>
                    </div>
                    <nav className="flex flex-col gap-2">
                        {visibleMenuItems.map(item => (
                            <NavLink key={item.path} to={item.path} onClick={() => setIsMobileMenuOpen(false)} 
                            className={({ isActive }) => `flex items-center gap-4 px-4 py-4 lg:justify-center xl:justify-start lg:px-0 xl:px-4 lg:py-3 rounded-xl transition-all relative ${isActive ? 'bg-primary text-white shadow-lg shadow-primary/20' : 'text-text-secondary hover:bg-background-card hover:text-fg'}`}>
                                <span className="material-symbols-outlined text-[28px] lg:text-[24px] shrink-0">{item.icon}</span>
                                <span className="lg:hidden xl:block text-base font-bold lg:text-sm lg:font-medium">{item.name}</span>
                                {item.badge && <div className="ml-auto flex items-center justify-center min-w-[24px] h-6 lg:h-5 px-2 lg:px-1.5 rounded-full bg-accent-red text-white text-xs lg:text-[10px] font-bold">{item.badge}</div>}
                            </NavLink>
                        ))}
                    </nav>
                </div>
                <div className="p-4 pb-8 lg:pb-4 border-t border-border-color shrink-0 flex items-center gap-2 lg:flex-col xl:flex-row">
                    <button onClick={() => { setIsProfileModalOpen(true); setIsMobileMenuOpen(false); }} className="w-full flex-1 min-w-0 lg:flex-none xl:flex-1 flex items-center justify-start lg:justify-center xl:justify-start gap-3 px-2 py-2 rounded-lg hover:bg-background-card transition-colors">
                        <div className="h-9 w-9 rounded-full bg-gray-600 flex items-center justify-center overflow-hidden ring-2 ring-border-color shrink-0">
                            {currentUser?.avatar ? <img src={currentUser.avatar} alt="" className="w-full h-full object-cover" /> : <span className="text-xs font-bold">{currentUser ? getInitials(currentUser.name) : '?'}</span>}
                        </div>
                        <div className="hidden xl:flex flex-col items-start min-w-0">
                            <p className="text-sm font-medium truncate w-full text-left">{currentUser?.name || t('sidebar.not_logged_in')}</p>
                        </div>
                    </button>
                    <ThemeToggleButton className="flex hover:bg-background-card" />
                </div>
            </aside>

            {/* MAIN CONTENT - scroll yönetimini buraya devrediyoruz */}
            <main className="flex-1 flex flex-col min-w-0 h-full relative isolate">
                
                {/* SMART HEADER - Sadece mobilde absolute olur ve kaybolur, tablette lg:hidden ile zaten gizlidir */}
                <header className={`lg:hidden absolute top-0 left-0 w-full flex shrink-0 items-center justify-between px-4 py-4 bg-background-card/95 backdrop-blur-md border-b border-border-color/30 z-[140] transition-transform duration-300 ease-in-out ${isHeaderVisible ? 'translate-y-0' : '-translate-y-full'}`}>
                    <div className="flex items-center gap-4">
                        <button onClick={() => setIsMobileMenuOpen(true)} className="p-3 -ml-3 text-fg hover:bg-fg/5 rounded-xl transition-all"><span className="material-symbols-outlined text-3xl">menu</span></button>
                        <div className="flex items-center gap-2"><Logo className="w-8 h-8 text-primary" /><span className="font-bold text-lg">{appSettings.appName}</span></div>
                    </div>
                    <div className="flex items-center gap-2">
                        <ThemeToggleButton className="flex hover:bg-background-main" />
                        {currentUser && (
                            <div className="size-10 rounded-full bg-gray-600 flex items-center justify-center overflow-hidden ring-1 ring-border-color cursor-pointer active:scale-95 transition-transform" onClick={() => setIsProfileModalOpen(true)}>
                                {currentUser.avatar ?
                                    <img src={currentUser.avatar} alt="" className="w-full h-full object-cover" /> :
                                    <span className="text-[10px] font-bold">{getInitials(currentUser.name)}</span>
                                }
                            </div>
                        )}
                    </div>
                </header>
            
                {/* Sayfa İçerikleri: Mobilde header absolute olduğu için pt-[73px] boşluk bırakır, Tablette (lg) sıfırlanır. 
                    Not: Scroll dinleme özelliği içeriğe (POS.tsx'e) devredildiği için flex-1 ile tam kapsayıcı yapıldı. 
                    Smart Header mantığını ürünlerin kaydırılmasına bağlamak için id atandı. */}
                <div 
                    id="main-layout-scroll-container"
                    ref={scrollContainerRef}
                    onScroll={handleMainScroll}
                    className="flex-1 overflow-y-auto overflow-x-hidden relative custom-scrollbar touch-pan-y min-h-0 pt-[73px] lg:pt-0"
                >
                    {children}
                </div>
            </main>

            {/* MODALS */}
            {isProfileModalOpen && (
                <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm" onClick={() => setIsProfileModalOpen(false)}>
                    <div className="bg-background-card w-full max-w-lg rounded-2xl border border-border-color shadow-2xl flex flex-col" onClick={e => e.stopPropagation()}>
                        <div className="p-6 border-b border-border-color flex justify-between items-center bg-background-main/30">
                            <h3 className="text-xl font-bold flex items-center gap-2"><span className="material-symbols-outlined text-primary">switch_account</span>{t('sidebar.switch_profile')}</h3>
                            <button onClick={() => setIsProfileModalOpen(false)}><span className="material-symbols-outlined">close</span></button>
                        </div>
                        <div className="p-6 grid grid-cols-1 sm:grid-cols-2 gap-3 max-h-[60vh] overflow-y-auto custom-scrollbar">
                            {staff.map(member => (
                                <button key={member.id} onClick={() => handleProfileClick(member)} className={`flex items-center gap-4 p-3 rounded-xl border transition-all ${currentUser?.id === member.id ? 'bg-primary/10 border-primary' : 'bg-background-main border-border-color hover:bg-background-card'}`}>
                                    <div className="w-12 h-12 rounded-full overflow-hidden bg-gray-700 border border-white/10 shrink-0">
                                        {member.avatar ? <img src={member.avatar} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center font-bold text-sm">{getInitials(member.name)}</div>}
                                    </div>
                                    <div className="min-w-0">
                                        <p className="font-bold text-sm truncate">{member.name}</p>
                                    </div>
                                </button>
                            ))}
                        </div>
                        <div className="p-4 border-t border-border-color bg-background-main/30 flex justify-end">
                            <button onClick={handleLogout} className="flex items-center gap-2 px-4 py-2 text-accent-red hover:bg-accent-red/10 border border-transparent hover:border-accent-red/30 transition-all text-sm font-bold">
                                <span className="material-symbols-outlined text-[18px]">logout</span>{t('sidebar.logout')}
                            </button>
                        </div>
                    </div>
                </div>
            )}

{isPinModalOpen && targetUser && (
                <div className="fixed inset-0 z-[210] flex items-center justify-center p-2 sm:p-4 bg-black/85 backdrop-blur-[10px] overflow-y-auto custom-scrollbar" onClick={() => setIsPinModalOpen(false)}>
                    {/* Premium Kapsayıcı: Yatayda Genişler, Cam Efekti ve Gölge Alır */}
                    <div className="bg-background-card/90 backdrop-blur-md w-full max-w-sm rounded-3xl border border-border-color/50 shadow-[0_0_60px_-10px_rgb(var(--shadow-strong))] p-6 
sm:p-8 flex flex-col items-center my-auto landscape:flex-row landscape:max-w-2xl landscape:gap-10 landscape:p-8 landscape:shadow-[0_0_90px_-15px_rgba(212,131,17,0.15)]" onClick={e => e.stopPropagation()}>
                    {/* Sol Panel: Premium Bilgi Alanı */}
                    <div className="mb-6 flex flex-col items-center shrink-0 landscape:mb-0 landscape:w-[240px] landscape:p-6 landscape:bg-background-main/30 landscape:rounded-2xl landscape:border landscape:border-border-color/30 landscape:shadow-inner">
                            <div className="w-20 h-20 rounded-full border-4 border-background-main shadow-2xl overflow-hidden mb-4 landscape:w-16 landscape:h-16 landscape:mb-3 landscape:ring-2 landscape:ring-primary/20">
                                {targetUser.avatar ?
                                    <img src={targetUser.avatar} alt="" className="w-full h-full object-cover" /> : 
                                    <div className="w-full h-full bg-gray-700 flex items-center justify-center font-bold text-2xl">{getInitials(targetUser.name)}</div>
                                }
                            </div>
                            <h3 className="text-xl font-extrabold text-fg text-center leading-tight tracking-tight landscape:text-lg landscape:mb-1">{targetUser.name}</h3>

                            {/* Pin Noktaları (Dikey Mod) - Tam Orta Hizalama Revize Edildi */}
                            <div className={`flex gap-4 mt-6 mb-14 landscape:hidden ${pinError ? 'animate-shake' : ''}`}>
                                {[0, 1, 2, 3].map(i => (
                                    <div key={i} className={`w-4 h-4 rounded-full border-2 transition-all duration-300 ${pinInput.length > i ? 'bg-primary border-primary scale-110 shadow-[0_0_15px_rgba(212,131,17,0.4)]' : 'bg-transparent border-border-color'} ${pinError ? 'border-accent-red bg-accent-red/50 animate-pulse' : ''}`} />
                                ))}
                            </div>

                            <div className="hidden landscape:block text-center mt-auto">
                                <span className="material-symbols-outlined text-4xl text-primary/40 animate-pulse">lock_open</span>
                                <p className="text-xs font-bold text-fg mt-1 uppercase tracking-tighter">Güvenli Giriş</p>
                                <p className="text-[10px] text-text-secondary mt-1 leading-relaxed opacity-70">Lütfen 4 haneli PIN kodunuzu tuşlayın.</p>
                            </div>
                        </div>

                        {/* Sağ Panel: Tuş Takımı ve Kontroller */}
                        <div className="flex-1 flex flex-col items-center w-full min-w-0">
                            {/* Pin Noktaları (Yatay Modda Buraya Taşınır) */}
                            <div className={`hidden landscape:flex gap-5 mb-8 ${pinError ? 'animate-shake' : ''}`}>
                                {[0, 1, 2, 3].map(i => (
                                    <div key={i} className={`w-4 h-4 rounded-full border-2 transition-all duration-300 ${pinInput.length > i ? 'bg-primary border-primary scale-110 shadow-[0_0_15px_rgba(212,131,17,0.4)]' : 'bg-transparent border-border-color'} ${pinError ? 'border-accent-red bg-accent-red/50 animate-pulse' : ''}`} />
                                ))}
                            </div>

                            <div className="grid grid-cols-3 gap-3 sm:gap-4 w-full max-w-[260px] sm:max-w-[280px] justify-items-center mx-auto landscape:max-w-[230px] landscape:gap-3">
                                {[1, 2, 3, 4, 5, 6, 7, 8, 9].map(num => (
                                    <button key={num} disabled={isVerifyingPin} onClick={() => handlePinInput(num.toString())} className="w-14 h-14 sm:w-16 sm:h-16 rounded-full flex items-center justify-center bg-background-main border border-border-color text-xl sm:text-2xl font-black text-fg shadow-xl hover:bg-fg/10 active:scale-90 transition-all landscape:w-12 landscape:h-12 landscape:text-xl landscape:border-fg/5 disabled:opacity-40 disabled:pointer-events-none">{num}</button>
                                ))}
                                <button disabled={isVerifyingPin} onClick={() => setIsPinModalOpen(false)} className="w-14 h-14 sm:w-16 sm:h-16 rounded-full flex items-center justify-center text-text-secondary hover:text-fg active:scale-90 transition-transform landscape:w-12 landscape:h-12 disabled:opacity-40 disabled:pointer-events-none"><span className="material-symbols-outlined text-2xl">close</span></button>
                                <button disabled={isVerifyingPin} onClick={() => handlePinInput('0')} className="w-14 h-14 sm:w-16 sm:h-16 rounded-full flex items-center justify-center bg-background-main border border-border-color text-xl sm:text-2xl font-black text-fg shadow-xl hover:bg-fg/10 active:scale-90 transition-all landscape:w-12 landscape:h-12 landscape:text-xl landscape:border-fg/5 disabled:opacity-40 disabled:pointer-events-none">0</button>
                                <button disabled={isVerifyingPin} onClick={handlePinBackspace} className="w-14 h-14 sm:w-16 sm:h-16 rounded-full flex items-center justify-center text-text-secondary hover:text-fg active:scale-90 transition-transform landscape:w-12 landscape:h-12 disabled:opacity-40 disabled:pointer-events-none"><span className="material-symbols-outlined text-2xl">backspace</span></button>
                            </div>

                            <button onClick={handlePinSubmit} disabled={isVerifyingPin} className="mt-6 sm:mt-10 w-full py-3 bg-gradient-to-r from-primary to-primary-hover text-white text-base sm:text-lg font-black rounded-2xl shadow-2xl shadow-primary/20 transition-all active:scale-[0.98] active:brightness-90 landscape:mt-6 landscape:py-2.5 landscape:text-sm disabled:opacity-70 flex items-center justify-center gap-2">
                                {isVerifyingPin ? (
                                    <>
                                        <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                                        Doğrulanıyor...
                                    </>
                                ) : 'Giriş Yap'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Layout;